/**
 * /checkin/onboarding — two-step onboarding after registration:
 *   Step 1: Choose how Dr. C greets you + how you usually greet Dr. C.
 *   Step 2: Confirm disclosure + optional email preferences → home.
 *
 * "Hey boo" is opt-in only — never the default. The user must select it
 * explicitly for it to be their preferred greeting.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import {
  CheckInShell,
  CheckInLogo,
  PrimaryButton,
  GhostButton,
  CHECKIN_PALETTE,
} from "./_shell";
import { checkinApi, getToken, setCachedUser } from "@/lib/checkinApi";

const GREETING_OPTIONS = [
  { key: "hi_first_name",       label: "Hi, [your name]",              preview: "Hi, {name}. I'm glad you checked in today. How are you doing—really?" },
  { key: "hey_first_name",      label: "Hey, [your name]",             preview: "Hey, {name}! I'm glad you're here. What's on your mind today?" },
  { key: "good_timeofday_name", label: "Good morning / afternoon / evening", preview: "Good morning, {name}. I'm glad you checked in today. How are you doing—really?" },
  { key: "hey_girl",            label: "Hey girl",                      preview: "Hey girl! I'm glad you checked in. Tell me what's going on." },
  { key: "hey_sis",             label: "Hey sis",                       preview: "Hey sis! I'm glad you checked in. Tell me what's going on." },
  { key: "hey_boo",             label: "Hey boo",                       preview: "Hey boo 💜 I'm glad you checked in today. How are you doing—really?" },
  { key: "name_only",           label: "Use my name without a greeting", preview: "{name}. I'm glad you checked in today. How are you doing—really?" },
  { key: "let_it_flow",         label: "Let the conversation develop naturally", preview: "I'm glad you checked in today, {name}. How are you doing—really?" },
];

const USER_GREETS_OPTIONS = [
  { key: "hey_dr_c",         label: "Hey Dr. C" },
  { key: "hi_dr_c",          label: "Hi Dr. C" },
  { key: "good_morning_dr_c",label: "Good morning Dr. C" },
  { key: "hey_girl",         label: "Hey girl" },
  { key: "hey_sis",          label: "Hey sis" },
  { key: "hey_boo",          label: "Hey boo" },
  { key: "something_else",   label: "Something else" },
];

export default function CheckInOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [me, setMe] = useState(null);
  const [greeting, setGreeting] = useState("hi_first_name");
  const [userGreets, setUserGreets] = useState("hey_dr_c");
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/checkin", { replace: true });
      return;
    }
    (async () => {
      try {
        const { user } = await checkinApi.me();
        setMe(user);
        if (user.greeting_style) setGreeting(user.greeting_style);
        if (user.user_greets_style) setUserGreets(user.user_greets_style);
      } catch {
        navigate("/checkin/signin", { replace: true });
      }
    })();
  }, [navigate]);

  const firstName = me?.first_name || "there";
  const preview = useMemo(() => {
    const opt = GREETING_OPTIONS.find((o) => o.key === greeting);
    return (opt?.preview || "").replace("{name}", firstName);
  }, [greeting, firstName]);

  async function saveGreetingAndContinue() {
    setErr("");
    setSaving(true);
    try {
      await checkinApi.setGreeting({
        greeting_style: greeting,
        user_greets_style: userGreets,
        custom_greeting: null,
      });
      setStep(2);
    } catch (e) {
      setErr(e.message || "Couldn't save your preferences.");
    } finally {
      setSaving(false);
    }
  }

  async function acceptConsentAndFinish() {
    setErr("");
    setSaving(true);
    try {
      await checkinApi.recordConsent({
        terms_version: "draft-1",
        privacy_version: "draft-1",
        companion_disclosure_version: "draft-1",
        marketing_opt_in: marketing,
      });
      // Refresh cached user then send to home.
      try {
        const { user } = await checkinApi.me();
        setCachedUser(user);
      } catch { /* non-fatal */ }
      navigate("/checkin/home", { replace: true });
    } catch (e) {
      setErr(e.message || "Couldn't record your consent.");
    } finally {
      setSaving(false);
    }
  }

  if (!me) {
    return (
      <CheckInShell testid="checkin-onboarding-loading">
        <div className="mt-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CHECKIN_PALETTE.purple }} />
        </div>
      </CheckInShell>
    );
  }

  return (
    <CheckInShell testid="checkin-onboarding">
      <div className="pt-2 flex items-center justify-between">
        <CheckInLogo small />
        <div className="text-[11px]" style={{ color: "#7A7288" }}>Step {step} of 2</div>
      </div>

      {step === 1 && (
        <div className="mt-6" data-testid="onboarding-step-greeting">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
               style={{ color: CHECKIN_PALETTE.purple }}>
            Set the tone
          </div>
          <h1 className="mt-2 font-serif text-3xl leading-tight"
              style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            How would you like Dr. C to greet you?
          </h1>
          <p className="mt-2 text-[13.5px]" style={{ color: "#5C5468" }}>
            We believe conversations should begin with acknowledgment, not just a question.
          </p>

          <div className="mt-5 space-y-2" data-testid="onboarding-greeting-list">
            {GREETING_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setGreeting(opt.key)}
                data-testid={`greeting-option-${opt.key}`}
                className="w-full text-left rounded-2xl px-4 py-3 transition-colors"
                style={{
                  border: `1.5px solid ${greeting === opt.key ? CHECKIN_PALETTE.purple : CHECKIN_PALETTE.silverSoft}`,
                  background: greeting === opt.key ? "rgba(92,30,138,0.04)" : "white",
                  color: CHECKIN_PALETTE.ink,
                  minHeight: 48,
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[14.5px] font-medium">{opt.label}</span>
                  {greeting === opt.key ? (
                    <Check className="w-4 h-4" style={{ color: CHECKIN_PALETTE.purple }} />
                  ) : null}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 rounded-2xl p-4"
               style={{ background: "rgba(92,30,138,0.05)", border: `1px dashed ${CHECKIN_PALETTE.purpleSoft}` }}
               data-testid="onboarding-greeting-preview">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                 style={{ color: CHECKIN_PALETTE.purple }}>Here&rsquo;s how your check-ins may begin</div>
            <p className="mt-2 text-[14px] leading-relaxed italic" style={{ color: "#2E2439" }}>
              &ldquo;{preview}&rdquo;
            </p>
          </div>

          <div className="mt-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
                 style={{ color: CHECKIN_PALETTE.purple }}>
              How do you usually greet Dr. C?
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="onboarding-user-greets-list">
              {USER_GREETS_OPTIONS.map((o) => (
                <button
                  key={o.key} type="button" onClick={() => setUserGreets(o.key)}
                  data-testid={`user-greets-option-${o.key}`}
                  className="rounded-full text-[13px] px-4 py-2"
                  style={{
                    border: `1.5px solid ${userGreets === o.key ? CHECKIN_PALETTE.purple : CHECKIN_PALETTE.silverSoft}`,
                    background: userGreets === o.key ? CHECKIN_PALETTE.purple : "white",
                    color: userGreets === o.key ? "white" : CHECKIN_PALETTE.ink,
                    minHeight: 40,
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {err ? (
            <div className="mt-4 rounded-2xl border px-4 py-3 text-sm"
                 style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}>
              {err}
            </div>
          ) : null}

          <div className="mt-6">
            <PrimaryButton
              onClick={saveGreetingAndContinue}
              disabled={saving}
              testid="onboarding-greeting-continue"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </span>
              ) : "Continue"}
            </PrimaryButton>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6" data-testid="onboarding-step-disclosure">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em]"
               style={{ color: CHECKIN_PALETTE.purple }}>
            Before you begin
          </div>
          <h1 className="mt-2 font-serif text-3xl leading-tight"
              style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            A quick, honest note.
          </h1>

          <div className="mt-4 space-y-3 text-[13.5px] leading-relaxed" style={{ color: "#3A3348" }}>
            <p>
              Check In with Dr. C uses technology trained on Dr. C&rsquo;s
              approved voice, frameworks, and resources to provide
              conversational guidance.
            </p>
            <p>
              Responses are not necessarily written live by Dr. C, and the
              service is not continuously monitored. It is designed for
              education, reflection, coaching support, and general guidance
              — not emergencies, diagnosis, therapy, or medical treatment.
            </p>
            <p>
              If you are in immediate danger, please contact local emergency
              services right away.
            </p>
          </div>

          <label className="mt-6 flex items-start gap-3 select-none"
                 data-testid="onboarding-marketing-optin">
            <input
              type="checkbox"
              className="mt-1 w-4 h-4"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              style={{ accentColor: CHECKIN_PALETTE.purple }}
              data-testid="onboarding-marketing-checkbox"
            />
            <span className="text-[12.5px] leading-relaxed" style={{ color: "#4A4256" }}>
              Send me occasional updates from Dr. C. I can change this any
              time. Optional.
            </span>
          </label>

          {err ? (
            <div className="mt-4 rounded-2xl border px-4 py-3 text-sm"
                 style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}>
              {err}
            </div>
          ) : null}

          <div className="mt-6 space-y-3">
            <PrimaryButton
              onClick={acceptConsentAndFinish}
              disabled={saving}
              testid="onboarding-consent-finish"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Getting your space ready…
                </span>
              ) : "I understand — take me in"}
            </PrimaryButton>
            <GhostButton onClick={() => setStep(1)} testid="onboarding-consent-back">
              Back
            </GhostButton>
          </div>
        </div>
      )}
    </CheckInShell>
  );
}
